<template>
  <SetupCard @click.capture="holdClick">
    <!-- Intro: what they came for, and what is about to happen. -->
    <template v-if="stage === 'intro'">
      <h2 class="setupTitle">{{ introTitle }}</h2>
      <div v-if="queueState.kind === 'person'" class="chip">
        <CompanyLogo
          v-if="personLogo && queueState.company"
          class="landed"
          :domain="queueState.company.domain"
          :name="queueState.company.name"
          :size="40"
          aria-hidden="true"
        />
        <span v-else class="avatar" :class="{ pending: personLogo === null }" aria-hidden="true">{{ personLogo === false ? initialsOf(queueState.name) : '' }}</span>
        <span class="chipText">
          <span class="chipName">{{ queueState.name }}</span>
          <span v-if="queueState.detail" class="chipSub">{{ queueState.detail }}</span>
        </span>
        <span class="chipMeta">{{ formatPhone(queueState.phone) }} · Mobile</span>
      </div>
      <div v-else-if="queueState.kind === 'people'" class="chip people">
        <span class="chipName queuedCount">{{ people(queueState.count) }} queued</span>
        <QueuedCompanies v-if="queueState.companies.length" class="queuedCompanies" :companies="queueState.companies" />
      </div>
      <ol class="steps">
        <li class="step"><span class="stepNum" aria-hidden="true">1</span><span class="stepText">{{ micPermission === 'granted' ? 'Check your microphone' : 'Allow your microphone' }}</span></li>
        <li v-if="callbackNumber !== null" class="step">
          <span class="stepNum done" aria-hidden="true"><AppIcon name="checkmark" :size="14" /></span>
          <span class="stepText">Callbacks ring {{ formatPhone(callbackNumber) }}</span><span class="stepMeta">Verified</span>
        </li>
        <li v-else class="step"><span class="stepNum" aria-hidden="true">2</span><span class="stepText">Verify the phone we forward callbacks to</span><span class="stepMeta">Optional</span></li>
        <li v-if="hasSelectStep" class="step"><span class="stepNum" aria-hidden="true">3</span><span class="stepText">Pick who to call first</span></li>
      </ol>
      <div class="actions">
        <button type="button" class="btn primary" @click="emit('start')">Let’s go<AppIcon name="arrow_right" :size="14" /></button>
      </div>
    </template>

    <!-- Microphone: the browser asks, and owns switching; the step names what it handed out. -->
    <template v-else-if="stage === 'mic'">
      <h2 class="setupTitle">{{ micTitle }}</h2>
      <MicCheck :allowed-earlier="micAllowedEarlier">
        <template #action>
          <button type="button" class="btn primary" @click="emit('continue-mic')">Continue<AppIcon name="arrow_right" :size="14" /></button>
        </template>
      </MicCheck>
    </template>

    <!-- Callback phone -->
    <template v-else-if="stage === 'phone'">
      <template v-if="verifyActive">
        <CallAndCodePanel
          :phase="verifyPhase"
          :phone-number="telephony.verify.phoneNumber"
          :code="telephony.verify.code"
          :seconds-left="telephony.verify.secondsLeft"
          :fail-reason="telephony.verify.failReason"
          @cancel="onCancel"
          @recall="onRecall"
        />
        <div v-if="verifyPhase === 'failed'" class="actions">
          <button type="button" class="linkBtn" @click="emit('skip-phone')">Skip for now</button>
        </div>
      </template>
      <template v-else>
        <span class="eyebrow">Optional</span>
        <h2 class="setupTitle">Where should callbacks reach you?</h2>
        <p class="setupCopy">When a prospect calls back, we forward the call here.</p>
        <FormField label="Your phone number" :error="phoneError">
          <PhoneInput v-model="phone" placeholder="+1 (206) 555-0182" @input="phoneError = ''" />
        </FormField>
        <p class="setupHint">We’ll call you with a code to enter on your keypad.</p>
        <div class="actions">
          <button type="button" class="linkBtn" @click="emit('skip-phone')">Skip for now</button>
          <button type="button" class="btn primary" @click="onVerify">
            <AppIcon name="phone_call" :size="14" />Verify my number
          </button>
        </div>
      </template>
    </template>

    <!-- Captured leads -->
    <template v-else-if="stage === 'select'">
      <h2 class="setupTitle">Select leads to call</h2>
      <CapturedLeadsList
        :leads="leads"
        :selected-count="selectedCount"
        :all-selected="allSelected"
        :disabled="adding"
        @toggle-lead="emit('toggle-lead', $event)"
        @toggle-all="emit('toggle-all')"
      />
      <div v-if="addError" class="failRow">
        <AppIcon name="error" :size="16" color="var(--color-danger)" /><span>{{ addError }}</span>
      </div>
      <div class="actions">
        <button type="button" class="linkBtn" :disabled="adding" @click="emit('skip-select')">Skip and find new leads in the extension</button>
        <button type="button" class="btn primary" :disabled="selectedCount === 0 || adding" @click="emit('add-selected')">
          {{ addLabel }}
        </button>
      </div>
    </template>

    <!-- Done -->
    <template v-else-if="stage === 'done'">
      <span class="doneBadge" aria-hidden="true"><AppIcon name="checkmark" :size="26" /></span>
      <h2 class="setupTitle">{{ doneTitle }}</h2>
      <div v-if="queueState.kind === 'person'" class="chip">
        <CompanyLogo
          v-if="personLogo && queueState.company"
          class="landed"
          :domain="queueState.company.domain"
          :name="queueState.company.name"
          :size="40"
          aria-hidden="true"
        />
        <span v-else class="avatar" :class="{ pending: personLogo === null }" aria-hidden="true">{{ personLogo === false ? initialsOf(queueState.name) : '' }}</span>
        <span class="chipText">
          <span class="chipName">{{ queueState.name }}</span>
          <span v-if="queueState.detail" class="chipSub">{{ queueState.detail }}</span>
        </span>
        <span class="chipMeta">{{ formatPhone(queueState.phone) }} · Mobile</span>
      </div>
      <p v-if="phoneSkipped" class="setupNote">You can verify your phone any time in Preferences.</p>
      <p v-if="queueState.kind === 'empty' && added === 0" class="setupNote">
        Capture people in the extension, or Power Dial a list, and they’ll be waiting in your queue.
      </p>
      <div class="actions">
        <button type="button" class="btn primary" :disabled="landing" :aria-busy="landing || undefined" @click="emit('finish')">
          <AppIcon name="phone_call" :size="14" />{{ doneButton }}
        </button>
      </div>
    </template>
  </SetupCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import CallAndCodePanel from './settings/CallAndCodePanel.vue'
import FormField from './settings/FormField.vue'
import PhoneInput from './settings/PhoneInput.vue'
import CompanyLogo from '../CompanyLogo.vue'
import CapturedLeadsList from './CapturedLeadsList.vue'
import QueuedCompanies from './QueuedCompanies.vue'
import MicCheck from './MicCheck.vue'
import SetupCard from './SetupCard.vue'
import { useMicPermission } from '../../composables/useMicPermission'
import { useTelephony } from '../../composables/useTelephony'
import type { OnboardingStage, QueueState } from '../../composables/useDialerOnboarding'
import type { SelectableLead } from '../../composables/useCapturedLeads'
import { INVALID_PHONE } from './settings/telephonyValidation'
import { formatPhone, toE164 } from '../../utils/phone'
import { initialsOf } from '../../utils/names'
import { hasFavicon } from '../../../services/favicons'

const props = defineProps<{
  stage: OnboardingStage
  queueState: QueueState
  leads: SelectableLead[]
  selectedCount: number
  allSelected: boolean
  added: number
  adding: boolean
  addError: string | null
  phoneSkipped: boolean
  callbackNumber: string | null
  micAllowedEarlier: boolean
  /** The queue is still landing the people just added: the done button waits for it. */
  landing?: boolean
}>()

const emit = defineEmits<{
  (e: 'start'): void
  (e: 'continue-mic'): void
  (e: 'skip-phone'): void
  (e: 'toggle-lead', leadId: string): void
  (e: 'toggle-all'): void
  (e: 'add-selected'): void
  (e: 'skip-select'): void
  (e: 'finish'): void
}>()

const { state: micState, permission: micPermission, device: micDevice } = useMicPermission()
const telephony = useTelephony()

const hasSelectStep = computed(() => props.leads.length > 0)


// The person's avatar is their company's logo when the favicon service has a real one,
// else their initials.
const personLogo = ref<boolean | null>(null)
watch(
  () => (props.queueState.kind === 'person' ? props.queueState.company : undefined),
  async (company) => {
    if (company === undefined) return
    personLogo.value = company ? await hasFavicon(company.domain) : false
  },
  { immediate: true },
)

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`)

const introTitle = computed(() => {
  const q = props.queueState
  if (q.kind === 'person') return `Before you call ${q.firstName}, let’s set up your dialer`
  if (q.kind === 'people') return 'Before you dial, let’s set up your dialer'
  return 'Set up your dialer'
})

const doneTitle = computed(() => {
  const q = props.queueState
  if (q.kind === 'person') return `You’re set. ${q.firstName} is ready to call.`
  if (q.kind === 'people') return `You’re set. ${people(q.count)} ${q.count === 1 ? 'is' : 'are'} waiting.`
  return props.added > 0
    ? `You’re set. ${people(props.added)} ${props.added === 1 ? 'is' : 'are'} in your queue.`
    : 'You’re all set'
})

const doneButton = computed(() => {
  const q = props.queueState
  if (q.kind === 'person') return `Call ${q.firstName} now`
  if (q.kind === 'people' || props.added > 0) return 'Start dialing'
  return 'Open my queue'
})

// ── Microphone ──
// By the site permission, not the mic: an allowed mic that fails to start still needs no Allow.
const micTitle = computed(() => {
  if (micState.value === 'granted') return micDevice.value === null ? 'Checking your microphone…' : 'Microphone ready'
  return micPermission.value === 'granted' ? 'Check your microphone' : 'Allow your microphone'
})

// ── Phone ──
const phone = ref('')
const phoneError = ref('')
const verifyPhase = computed(() => telephony.verify.phase as 'calling' | 'waiting' | 'failed' | 'verified')
const verifyActive = computed(() => telephony.verify.phase !== 'idle' && telephony.verify.target === 'own')

function onVerify(): void {
  const num = toE164(phone.value)
  if (!num) {
    phoneError.value = INVALID_PHONE
    return
  }
  void telephony.startCallbackVerification({ phone_number: num, target: 'own' })
}
function onCancel(): void {
  void telephony.cancelVerification('own')
}
function onRecall(): void {
  void telephony.recall('own')
}

// Whatever the card shows next takes clicks only after a beat: its button sits where the last one was,
// and the second click of a double-click must not press it ("Call Jordan now").
const ARM_MS = 500
let armedAt = 0
const micNamed = computed(() => micState.value === 'granted' && micDevice.value !== null)
watch(
  [() => props.stage, micNamed, verifyActive, () => telephony.verify.phase],
  () => {
    armedAt = performance.now() + ARM_MS
  },
  { flush: 'sync' },
)
// In the capture phase, so buttons and checkboxes alike are stopped before they act.
function holdClick(e: MouseEvent): void {
  if (performance.now() >= armedAt) return
  e.preventDefault()
  e.stopPropagation()
}

// ── Captured leads ──
const addLabel = computed(() =>
  props.adding ? 'Adding…' : props.selectedCount > 0 ? `Add ${props.selectedCount} to my call list` : 'Add to my call list',
)
</script>

<style scoped>
/* The card shell and its typography live in SetupCard. */

/* Who they came for, or who is waiting. */
.chip { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-4); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md); background: var(--color-bg-app); }
.chipText { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.chipName { font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.queuedCount { white-space: nowrap; }
.queuedCompanies { margin-left: auto; }
.chipSub { font-size: var(--fs-sm); color: var(--color-text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chipMeta { margin-left: auto; font-size: var(--fs-sm); color: var(--color-text-secondary); white-space: nowrap; }
.avatar { width: 40px; height: 40px; border-radius: var(--radius-full); background: var(--color-scheduled-soft); color: var(--color-scheduled); display: inline-flex; align-items: center; justify-content: center; font-size: var(--fs-sm); font-weight: var(--fw-semibold); flex-shrink: 0; }
.avatar.pending { background: transparent; }
.landed { animation: landIn var(--duration-fast) var(--ease-out); }
@keyframes landIn { from { opacity: 0; } }

.steps { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: var(--space-3); }
.step { display: flex; align-items: center; gap: var(--space-3); }
.stepNum { width: 26px; height: 26px; border-radius: var(--radius-full); background: var(--color-bg-subtle); color: var(--color-text-primary); display: inline-flex; align-items: center; justify-content: center; font-size: var(--fs-sm); font-weight: var(--fw-semibold); flex-shrink: 0; }
.stepText { font-weight: var(--fw-medium); }
.stepMeta { margin-left: auto; font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.stepNum.done { background: var(--color-success-soft); color: var(--color-success); }

.doneBadge { width: 56px; height: 56px; border-radius: var(--radius-full); background: var(--color-success-soft); color: var(--color-success); display: inline-flex; align-items: center; justify-content: center; }

/* Too narrow for one line: the companies take their own row, held from the start. */
@container dialer (max-width: 600px) {
  .chip.people { flex-direction: column; align-items: flex-start; gap: var(--space-2); }
  .chip.people .queuedCompanies { margin-left: 0; }
}
</style>
